import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { UserAuthService } from './user-auth.service';
import { StorageService } from './storage.service';
import { LanguageEnum } from 'node_js/models/enums/language';
import { User } from 'node_js/models/tables/user';
import { setUiTextTranslator } from 'src/global';

export interface LanguageClient {
  id: LanguageEnum,
  name: string,
  abbreviation: string
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  languagesStructure: LanguageClient[] = [
    { id: LanguageEnum.ENGLISH, name: 'English', abbreviation: 'en' },
    { id: LanguageEnum.FRENCH, name: 'Français', abbreviation: 'fr' }
  ];

  defaultLanguage = LanguageEnum.ENGLISH;
  activeLanguage: LanguageClient = null;

  public activeLanguageObservable: BehaviorSubject<LanguageClient | null>;

  private readonly fragmentTranslationKeys = [
    'Lab:',
    'Gadget:',
    'Role:',
    'Points:',
    'Avg:',
    'Friend Code:',
    'Next Date:',
    'Slide ',
    'Spread '
  ];
  private literalPatternEntries: { key: string, regex: RegExp }[] | null = null;

  constructor(
    private translate: TranslateService,
    private userAuthService: UserAuthService,
    private storageService: StorageService,
    private ngZone: NgZone,
  ) {
    this.activeLanguageObservable = new BehaviorSubject<LanguageClient | null>(null);

    this.translate.addLangs(this.languagesStructure.map(language => language.abbreviation));
    this.translate.setDefaultLang('en');
    setUiTextTranslator((text: string) => this.translateUiLiteral(text));

    this.translate.use('none').subscribe(() => {
      this.userAuthService.fAuthStateChangedObservable.subscribe(() => {
        if (this.userAuthService.didLoginCheck !== true) {
          return;
        }

        let user: User = null;
        if (this.userAuthService.loggedIn) {
          user = this.userAuthService.currentUserComplete.user;
        }

        new Promise<LanguageEnum>((resolve) => {
          if (user && user.lastLanguageId != null) {
            resolve(user.lastLanguageId);
            return;
          }

          this.getLanguageFromLocalStorage().then((languageId: LanguageEnum) => {
            if (languageId == null || languageId == undefined) {
              languageId = this.defaultLanguage;
            }
            resolve(languageId);
          }).catch((err: string) => {
            console.log('error getting language from local storage', err);
            resolve(this.defaultLanguage);
          });
        }).then((languageId: LanguageEnum) => {
          if (languageId) {
            this.setLanguage(languageId);
          }
        });
      });
    });
  }

  public get availableLanguages() {
    return this.languagesStructure;
  }

  public getLanguageFromLocalStorage() {
    return this.storageService.getFromLocalStorage('languageId');
  }

  public setLanguageLocalStorage(languageId: number) {
    this.storageService.setLocalStorage('languageId', languageId);
  }

  public async setLanguage(languageId: number) {
    if (languageId == null || languageId == undefined) {
      languageId = this.defaultLanguage;
    }

    const nextLanguage = this.languagesStructure.find(language => language.id == languageId) || this.languagesStructure[0];
    this.activeLanguage = nextLanguage;

    this.translate.use(this.activeLanguage.abbreviation).subscribe(() => {
      this.ngZone.run(() => {
        this.activeLanguageObservable.next(this.activeLanguage);
        this.userAuthService.currentLang = this.activeLanguage.id;

        if (this.userAuthService.currentUserComplete?.user) {
          this.userAuthService.currentUserComplete.user.lastLanguageId = this.activeLanguage.id;
        }
      });
    });

    this.setLanguageLocalStorage(languageId);
    if (this.userAuthService.loggedIn) {
      await this.userAuthService.updateLastLanguageId(languageId);
    }
  }

  public instant(key: string, params?: any) {
    const translation = this.translate.instant(key, params);
    if (typeof translation === 'string' && translation.length > 0) {
      return translation;
    }

    return key;
  }

  public translateUiLiteral(value: string) {
    if (typeof value !== 'string') {
      return value;
    }
    return this.translateUiLiteralForLanguage(value, this.activeLanguage?.id ?? this.defaultLanguage);
  }

  public isUiLiteralTranslationOf(source: string, candidate: string) {
    if (!source || !candidate) {
      return false;
    }

    if (candidate === source) {
      return true;
    }

    return this.languagesStructure.some(language => this.translateUiLiteralForLanguage(source, language.id) === candidate);
  }

  private translateUiLiteralForLanguage(value: string, languageId: number) {
    if (typeof value !== 'string' || !value) {
      return value;
    }

    const translations = this.getTranslationsForLanguage(languageId);
    const exactTranslation = translations[value];
    if (typeof exactTranslation === 'string' && exactTranslation.length > 0) {
      return exactTranslation;
    }

    const patternTranslation = this.translatePatternLiteral(value, translations);
    if (patternTranslation !== value) {
      return patternTranslation;
    }

    let translatedValue = value;
    for (const fragmentKey of this.fragmentTranslationKeys) {
      if (!translatedValue.includes(fragmentKey)) {
        continue;
      }

      const translatedFragment = translations[fragmentKey];
      if (typeof translatedFragment === 'string' && translatedFragment.length > 0 && translatedFragment !== fragmentKey) {
        translatedValue = translatedValue.replace(fragmentKey, translatedFragment);
      }
    }

    return translatedValue;
  }

  private translatePatternLiteral(value: string, translations: Record<string, string>) {
    for (const entry of this.getLiteralPatternEntries()) {
      const match = value.match(entry.regex);
      if (!match) {
        continue;
      }

      const translatedTemplate = translations[entry.key] || entry.key;
      return translatedTemplate.replace(/\{\{param(\d+)\}\}/g, (_token, paramIndex) => {
        const matchIndex = Number(paramIndex);
        return match[matchIndex] ?? '';
      });
    }

    return value;
  }

  private getLiteralPatternEntries() {
    if (this.literalPatternEntries) {
      return this.literalPatternEntries;
    }

    const englishTranslations = this.getTranslationsForLanguage(LanguageEnum.ENGLISH);
    this.literalPatternEntries = Object.keys(englishTranslations)
      .filter(key => key.includes('{{param'))
      .sort((first, second) => second.length - first.length)
      .map(key => ({
        key,
        regex: new RegExp(`^${this.escapeRegex(key).replace(/\\\{\\\{param\d+\\\}\\\}/g, '(.+?)')}$`)
      }));

    return this.literalPatternEntries;
  }

  private getTranslationsForLanguage(languageId: number) {
    const language = this.languagesStructure.find(item => item.id == languageId) || this.languagesStructure[0];
    return (((this.translate as any).store?.translations?.[language.abbreviation]) || {}) as Record<string, string>;
  }

  private escapeRegex(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  //If we need a translation when the page is loading (constructor, ngOnInit, afterViewInit, etc), make sure async is set to true
  //This will make sure that the translated value is given after this service has time to load our english and french json files.
  public getTranslation(key: string, async: true): Promise<string>;
  public getTranslation(key: string, async: false): string;
  public getTranslation(key: string, async: boolean): Promise<string> | string {
    if (async == true) {
      return new Promise<string>((resolve) => {
        this.translate.stream(key).subscribe((translation: string) => {
          if (this.activeLanguage?.id != null) {
            resolve(translation);
          }
        });
      });
    }

    return this.instant(key);
  }

  public getTranslationWithParams(key: string, paramsArray: any, async: true): Promise<string>;
  public getTranslationWithParams(key: string, paramsArray: any, async: false): string;
  public getTranslationWithParams(key: string, paramsArray: any, async: boolean): Promise<string> | string {
    if (paramsArray != null && paramsArray.length > 0) {
      const params: any = {};

      for (let i = 0; i < paramsArray.length; i++) {
        params['param' + (i + 1)] = paramsArray[i];
      }
      if (async == true) {
        return new Promise<string>((resolve) => {
          this.translate.stream(key, params).subscribe((translation: string) => {
            if (this.activeLanguage?.id != null) {
              resolve(translation);
            }
          });
        });
      }

      return this.instant(key, params);
    }

    if (async == true) {
      return this.getTranslation(key, true);
    }

    return this.getTranslation(key, false);
  }
}
